/**
 * stepAdvancer.ts
 *
 * Scheduled step advancement for hr_v1 cycles (CLAUDE.md §G.3 last bullet):
 *   - If Step 6 elapsed (appraisals manager-signed > 2 days ago) AND no
 *     open appeal, advance cycle to Step 7 (Outcome).
 *
 * This module exposes a single callable: `advanceCyclesIfDue()` that walks
 * active hr_v1 cycles and updates currentStep where due. It is intended
 * to be invoked from a scheduled Cloud Function (e.g. every hour). The
 * function deploy is OUT OF SCOPE for this repo — the file in
 * `functions/src/scheduled.ts` (when created) should call this routine.
 */

import {
  doc, getDocs, query, where, collection, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ReviewCycle } from '../types';
import { APPEAL_FILING_WINDOW_DAYS } from '../types/appeal';
import { listAppraisalsForCycle } from './appraisalService';
import { listOpenAppealsForCycle } from './appealService';

export interface AdvancementResult {
  cycleId: string;
  fromStep: number | undefined;
  toStep: number | undefined;
  reason: 'window_passed' | 'all_appraisals_closed' | 'no_change';
  notes?: string;
}

const WINDOW_MS = APPEAL_FILING_WINDOW_DAYS * 24 * 60 * 60 * 1000;

/**
 * Walk every hr_v1 cycle at currentStep 5 or 6. Advance any cycle where:
 *   - Every appraisal in the cycle is manager_signed or closed
 *   - The latest manager-signed timestamp is older than 2 days
 *   - No appeal is open
 * Idempotent.
 */
export async function advanceCyclesIfDue(): Promise<AdvancementResult[]> {
  const cyclesSnap = await getDocs(
    query(
      collection(db, 'reviewCycles'),
      where('frameworkVersion', '==', 'hr_v1'),
      where('currentStep', 'in', [5, 6]),
    ),
  );
  const results: AdvancementResult[] = [];

  for (const cdoc of cyclesSnap.docs) {
    const cycle = cdoc.data() as ReviewCycle;
    const cycleId = cdoc.id;
    const fromStep = cycle.currentStep;
    const appraisals = await listAppraisalsForCycle(cycleId);
    if (appraisals.length === 0) {
      results.push({ cycleId, fromStep, toStep: fromStep, reason: 'no_change',
        notes: 'No appraisals yet.' });
      continue;
    }
    const allSigned = appraisals.every(
      a => a.status === 'manager_signed' || a.status === 'closed',
    );
    if (!allSigned) {
      results.push({ cycleId, fromStep, toStep: fromStep, reason: 'no_change',
        notes: 'Some appraisals not yet manager-signed.' });
      continue;
    }
    const latestSign = appraisals.reduce<number>((max, a) => {
      const t = a.managerSignature?.signedAt.toDate().getTime() ?? 0;
      return t > max ? t : max;
    }, 0);
    const windowEnded = Date.now() - latestSign >= WINDOW_MS;
    if (!windowEnded) {
      results.push({ cycleId, fromStep, toStep: fromStep, reason: 'no_change',
        notes: 'Appeal window still open.' });
      continue;
    }
    const openAppeals = await listOpenAppealsForCycle(cycleId);
    if (openAppeals.length > 0) {
      results.push({ cycleId, fromStep, toStep: fromStep, reason: 'no_change',
        notes: `${openAppeals.length} appeal(s) still open.` });
      continue;
    }
    await updateDoc(doc(db, 'reviewCycles', cycleId), {
      currentStep: 7,
      updatedAt: serverTimestamp(),
    });
    results.push({ cycleId, fromStep, toStep: 7, reason: 'window_passed' });
  }
  return results;
}
